import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ContatoMaster {
  cnpj: string;
  cliente_id?: string;
  contato_real_nome?: string;
  contato_real_telefone?: string;
  contato_real_cargo?: string;
  contato_real_email?: string;
  contato_real_lead_id?: string;
  contato_real_atualizado_em?: string;
  gestor_nome?: string;
  gestor_cpf?: string;
  gestor_rg?: string;
  gestor_telefone?: string;
  gestor_email?: string;
  cessionario_nome?: string;
  cessionario_cpf?: string;
  cessionario_telefone?: string;
  cessionario_whatsapp?: string;
  whatsapp_principal?: string;
  whatsapp_fonte?: string;
}

// Normalizar CNPJ (sempre!)
const normalizeCnpj = (cnpj: string) => cnpj?.replace(/\D/g, '') || '';

/**
 * Hook declarativo para buscar contato master de um CNPJ específico
 * QueryKey: ['cnpj-contato-master', cnpjLimpo]
 */
export function useCnpjContatoMaster(cnpj?: string | null) {
  const cnpjLimpo = normalizeCnpj(cnpj || '');
  const enabled = cnpjLimpo.length === 14;

  const query = useQuery({
    queryKey: ['cnpj-contato-master', cnpjLimpo],
    queryFn: async (): Promise<ContatoMaster | null> => {
      const { data, error } = await supabase
        .from('cnpj_contato_master')
        .select('*')
        .eq('cnpj', cnpjLimpo)
        .maybeSingle();
      if (error) { 
        console.error('[useCnpjContatoMaster] Erro:', error); 
        return null; 
      }
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 15 * 60 * 1000,   // 15 minutos
  });

  return { 
    master: query.data, 
    isLoading: query.isLoading, 
    refetch: query.refetch 
  };
}

/**
 * Hook imperativo para buscar contato master (para Discadora e uso em callbacks)
 * Usa queryClient.fetchQuery para aproveitar cache
 */
export function useCnpjContatoMasterFetch() {
  const queryClient = useQueryClient();

  const fetchMaster = async (cnpj: string): Promise<ContatoMaster | null> => {
    const cnpjLimpo = normalizeCnpj(cnpj);
    if (cnpjLimpo.length !== 14) return null;
    try {
      return await queryClient.fetchQuery({
        queryKey: ['cnpj-contato-master', cnpjLimpo],
        queryFn: async () => {
          const { data, error } = await supabase
            .from('cnpj_contato_master')
            .select('*')
            .eq('cnpj', cnpjLimpo)
            .maybeSingle();
          if (error) throw error;
          return data;
        },
        staleTime: 5 * 60 * 1000,
      });
    } catch (error) {
      console.error('[useCnpjContatoMasterFetch] Erro:', error);
      return null;
    }
  };

  // Sempre normaliza antes de invalidar
  const invalidateMaster = (cnpj: string) => {
    const cnpjLimpo = normalizeCnpj(cnpj);
    queryClient.invalidateQueries({ queryKey: ['cnpj-contato-master', cnpjLimpo] });
  };

  // Refetch com await para telas críticas
  const refetchMaster = async (cnpj: string) => {
    const cnpjLimpo = normalizeCnpj(cnpj);
    await queryClient.refetchQueries({ queryKey: ['cnpj-contato-master', cnpjLimpo] });
  };

  return { fetchMaster, invalidateMaster, refetchMaster };
}
